import type { ISODate, QualityFlag } from "./types";

export interface SettlementObservation {
  readonly date: ISODate;
  readonly value: number;
  readonly settlementTimestamp?: string;
  readonly qualityFlags?: readonly QualityFlag[];
  readonly volume?: number | null;
  readonly openInterest?: number | null;
}
export interface FxAvailability {
  readonly date: ISODate;
  readonly rate: number;
  readonly valid?: boolean;
}

export type ExclusionReason =
  | "missing-leg-a"
  | "missing-leg-b"
  | "missing-fx"
  | "invalid-fx"
  | "invalid-leg-a-price"
  | "invalid-leg-b-price"
  | "duplicate-leg-a"
  | "duplicate-leg-b"
  | `leg-a:${QualityFlag}`
  | `leg-b:${QualityFlag}`;

export interface EligiblePairObservation {
  readonly date: ISODate;
  readonly legA: SettlementObservation;
  readonly legB: SettlementObservation;
  readonly fxRate?: number;
}

export interface ExcludedObservation {
  readonly date: ISODate;
  readonly reasons: readonly ExclusionReason[];
}

export interface AlignmentResult {
  readonly rule: "same-exchange-business-date-intersection-no-forward-fill";
  readonly eligible: readonly EligiblePairObservation[];
  readonly excluded: readonly ExcludedObservation[];
}

export interface AlignmentOptions {
  readonly requireFx?: boolean;
  readonly fx?: readonly FxAvailability[];
  readonly excludeLowLiquidity?: boolean;
  readonly excludeSourceWarnings?: boolean;
}

const ALWAYS_EXCLUDED_FLAGS: ReadonlySet<QualityFlag> = new Set([
  "stale",
  "duplicate",
  "abnormal-jump",
  "invalid-contract-metadata",
  "settlement-misaligned",
  "back-adjusted",
]);

function indexByDate(
  observations: readonly SettlementObservation[],
): Map<ISODate, readonly SettlementObservation[]> {
  const result = new Map<ISODate, SettlementObservation[]>();
  for (const observation of observations) {
    const existing = result.get(observation.date) ?? [];
    existing.push(observation);
    result.set(observation.date, existing);
  }
  return result;
}

function excludedFlags(
  observation: SettlementObservation,
  options: AlignmentOptions,
): readonly QualityFlag[] {
  return (observation.qualityFlags ?? []).filter((flag) =>
    ALWAYS_EXCLUDED_FLAGS.has(flag) ||
    (options.excludeLowLiquidity === true && (flag === "low-volume" || flag === "low-open-interest")) ||
    (options.excludeSourceWarnings === true && flag === "source-warning"),
  );
}

/** Exact date intersection. Missing exchange holidays are never synthesized. */
export function alignSameDateIntersection(
  legA: readonly SettlementObservation[],
  legB: readonly SettlementObservation[],
  options: AlignmentOptions = {},
): AlignmentResult {
  const aByDate = indexByDate(legA);
  const bByDate = indexByDate(legB);
  const fxByDate = new Map((options.fx ?? []).map((fx) => [fx.date, fx]));
  const dates = [...new Set([...aByDate.keys(), ...bByDate.keys()])].sort();
  const eligible: EligiblePairObservation[] = [];
  const excluded: ExcludedObservation[] = [];

  for (const date of dates) {
    const aRows = aByDate.get(date) ?? [];
    const bRows = bByDate.get(date) ?? [];
    const reasons: ExclusionReason[] = [];

    if (aRows.length === 0) reasons.push("missing-leg-a");
    if (bRows.length === 0) reasons.push("missing-leg-b");
    if (aRows.length > 1) reasons.push("duplicate-leg-a");
    if (bRows.length > 1) reasons.push("duplicate-leg-b");

    const a = aRows[0];
    const b = bRows[0];
    if (a && (!Number.isFinite(a.value) || a.value <= 0)) reasons.push("invalid-leg-a-price");
    if (b && (!Number.isFinite(b.value) || b.value <= 0)) reasons.push("invalid-leg-b-price");
    if (a) reasons.push(...excludedFlags(a, options).map((flag) => `leg-a:${flag}` as const));
    if (b) reasons.push(...excludedFlags(b, options).map((flag) => `leg-b:${flag}` as const));

    const fx = fxByDate.get(date);
    if (options.requireFx === true) {
      if (!fx) reasons.push("missing-fx");
      else if (fx.valid === false || !Number.isFinite(fx.rate) || fx.rate <= 0) reasons.push("invalid-fx");
    }

    if (reasons.length > 0 || !a || !b) {
      excluded.push({ date, reasons: [...new Set(reasons)] });
      continue;
    }

    eligible.push({ date, legA: a, legB: b, ...(fx ? { fxRate: fx.rate } : {}) });
  }

  return {
    rule: "same-exchange-business-date-intersection-no-forward-fill",
    eligible,
    excluded,
  };
}

/**
 * Adds a stale flag after a configurable run of unchanged eligible quotes.
 * It does not create observations or turn missing holidays into zero returns.
 */
export function flagConsecutiveStalePrices(
  observations: readonly SettlementObservation[],
  maximumUnchangedDays = 3,
): readonly SettlementObservation[] {
  if (!Number.isInteger(maximumUnchangedDays) || maximumUnchangedDays < 1) {
    throw new Error("maximumUnchangedDays must be a positive integer.");
  }
  const sorted = [...observations].sort((a, b) => a.date.localeCompare(b.date));
  let previous: number | undefined;
  let unchangedRun = 0;

  return sorted.map((observation) => {
    if (previous !== undefined && observation.value === previous) unchangedRun += 1;
    else unchangedRun = 0;
    previous = observation.value;

    if (unchangedRun < maximumUnchangedDays) return observation;
    return {
      ...observation,
      qualityFlags: [...new Set([...(observation.qualityFlags ?? []), "stale" as const])],
    };
  });
}
